import { z } from 'zod';

import { coreIds } from './ids.js';
import type { ResourceOwnerType } from './resource-ownership.types.js';
import { secretAliasNameSchema } from './secret.types.js';

/**
 * Shared schemas for the model-provider secrets surface — the wrapper
 * over user/team/org-tier `secrets` rows that the explicit-selection
 * chat + agent-runner paths address by `providerSecretId` (`mps_*` id).
 *
 * Server is the source of truth for these schemas; web imports them
 * via `@reflex/shared` (or `@/lib/types` in the web workspace) for
 * request bodies and list responses.
 */

export const ModelProviderSchema = z.enum([
  'anthropic',
  'openai',
  'xai',
  'google',
  'openrouter',
  'cursor',
  'fireworks-ai',
  'baseten',
  'vercel',
  'nebius',
  'runloop',
]);
export type ModelProvider = z.infer<typeof ModelProviderSchema>;

/** Providers for which users may persist their own model-provider secret. */
export const PersistableModelProviderSchema = ModelProviderSchema.exclude(['runloop']);
export type PersistableModelProvider = z.infer<typeof PersistableModelProviderSchema>;

/** Fast type guard for provider checks that do not need parsed data or validation errors. */
export function isPersistableModelProvider(
  provider: unknown,
): provider is PersistableModelProvider {
  return z.validate(PersistableModelProviderSchema, provider);
}

export const ModelProviderSecretTypeSchema = z.enum(['apiKey', 'subscription']);
export type ModelProviderSecretType = z.infer<typeof ModelProviderSecretTypeSchema>;

export const ModelProviderSecretScopeSchema = z.enum(['org', 'team', 'user']);
export type ModelProviderSecretScope = z.infer<typeof ModelProviderSecretScopeSchema>;

export const ModelProviderSecretIdSchema = coreIds.schema('modelProviderSecret');

/**
 * Strict name validator for **user-entered** row names (create/update
 * request bodies, UI dialogs). Built by {@link secretAliasNameSchema} so
 * the same alias rules apply across user-tier subscriptions
 * ({@link CredentialAliasSchema}) and the generic model-provider surface
 * — the two schemas share one source of truth for the `__`-free invariant
 * and the `[a-zA-Z0-9_-]+` alphabet. Server-generated rows (subscription
 * mirrors) are NOT validated against this — see
 * {@link ModelProviderSecretLabelSchema}.
 */
export const ModelProviderSecretNameSchema = secretAliasNameSchema({ label: 'Name' });
export type ModelProviderSecretName = z.infer<typeof ModelProviderSecretNameSchema>;

/**
 * Permissive validator for the **stored** display label returned on a
 * row. This is a superset of {@link ModelProviderSecretNameSchema}: it
 * accepts everything users can type plus the curated product labels the
 * server mints for subscription mirror rows (e.g. `"Claude Max"`,
 * `"Claude Max (work)"`), which contain spaces and parentheses the
 * strict input regex deliberately forbids. The wire/response schema must
 * accept these or list endpoints 400 on serialization.
 */
export const ModelProviderSecretLabelSchema = z
  .string()
  .min(1, 'Name cannot be empty')
  .max(96, 'Name must be 96 characters or fewer');
export type ModelProviderSecretLabel = z.infer<typeof ModelProviderSecretLabelSchema>;

export const ModelProviderSecretSchema = z
  .object({
    id: ModelProviderSecretIdSchema,
    organizationId: z.string(),
    ownerId: z.string().nullable(),
    scope: ModelProviderSecretScopeSchema,
    provider: ModelProviderSchema,
    type: ModelProviderSecretTypeSchema,
    name: ModelProviderSecretLabelSchema,
    /** `sec_*` id of the underlying ciphertext row; never returned with a value. */
    secretId: coreIds.schema('secret'),
    /**
     * Epoch-ms expiry of a subscription credential's access token, decoded from
     * its JWT `exp` at register/refresh. Null for `apiKey` rows and for any
     * subscription whose token has no decodable `exp`.
     */
    tokenExpiresAt: z.number().int().nonnegative().nullable(),
    /**
     * Optional custom base URL for providers that expose a base-URL env var
     * (Anthropic, OpenAI — see {@link MODEL_PROVIDER_BASE_URL_ENV_VARS}). When
     * set, the agent devbox receives the provider's base-URL env var at launch,
     * and the launch gateway upstream is pointed here when gateway protection is
     * active. Null for rows that target the provider's public API.
     */
    baseUrl: z.string().url().nullable(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  // Tagged so the OpenAPI generator emits one reusable `ModelProviderSecret`
  // component referenced by the model-provider-secret routes. See
  // docs/internal/architecture/api-contract.md.
  .meta({ id: 'ModelProviderSecret' });
export type ModelProviderSecret = z.infer<typeof ModelProviderSecretSchema>;

/**
 * Non-sensitive display subset of a {@link ModelProviderSecretSchema} — the
 * fields safe to surface on org-wide reporting surfaces (e.g. the costs
 * dashboard) without exposing the underlying ciphertext reference
 * (`secretId`) or value. Used by the org-scoped `getMetadataById` lookup so
 * every key that appears in an org's spend can be labelled, including
 * user- and team-tier keys owned by someone other than the viewer.
 */
export const ModelProviderSecretMetadataSchema = ModelProviderSecretSchema.pick({
  id: true,
  provider: true,
  name: true,
  scope: true,
  type: true,
  ownerId: true,
});
export type ModelProviderSecretMetadata = z.infer<typeof ModelProviderSecretMetadataSchema>;

export const ModelProviderSecretsListResponseSchema = z.object({
  secrets: z.array(ModelProviderSecretSchema),
});
export type ModelProviderSecretsListResponse = z.infer<
  typeof ModelProviderSecretsListResponseSchema
>;

/**
 * Common base for create requests. The `scope` + `ownerId` shape is
 * filled in by the route based on the URL it's mounted at (org/team/user),
 * not by the request body — clients pass `provider`, `type`, `name`,
 * `value` and the server decides where it lands.
 */
const CreateRequestBase = {
  provider: PersistableModelProviderSchema,
  type: ModelProviderSecretTypeSchema,
  name: ModelProviderSecretNameSchema,
  value: z.string().min(1, 'Value cannot be empty'),
  baseUrl: z.string().url().nullable().optional(),
} as const;

export const CreateModelProviderSecretRequestSchema = z.object(CreateRequestBase);
export type CreateModelProviderSecretRequest = z.infer<
  typeof CreateModelProviderSecretRequestSchema
>;

export const UpdateModelProviderSecretRequestSchema = z
  .object({
    name: ModelProviderSecretNameSchema.optional(),
    value: z.string().min(1).optional(),
    baseUrl: z.string().url().nullable().optional(),
  })
  .refine((v) => v.name !== undefined || v.value !== undefined || v.baseUrl !== undefined, {
    message: 'At least one of `name`, `value`, or `baseUrl` must be provided',
  });
export type UpdateModelProviderSecretRequest = z.infer<
  typeof UpdateModelProviderSecretRequestSchema
>;

/**
 * Maps a resource's owner kind onto the set of model-provider secret
 * scopes ({@link ModelProviderSecretScope}, which uses `org`) that the
 * resource may bind a key from. The rule narrows as ownership broadens:
 *
 * - `user`-owned   -> `user` + `team` + `org` keys
 * - `team`-owned   -> `team` + `org` keys
 * - everything else (`organization`, plugin-contributed kinds like
 *   `project`) -> `org` keys only.
 *
 * Returned scopes are ordered most-specific-first to match the existing
 * resolution precedence (user -> team -> org). Accepting `string` for
 * plugin-contributed kinds keeps the host's narrowing rules generic —
 * a plugin-owned kind defaults to org-tier keys without a host enum
 * branch.
 */
export function allowedSecretScopesForOwner(
  ownerType: ResourceOwnerType | string,
): ModelProviderSecretScope[] {
  switch (ownerType) {
    case 'user':
      return ['user', 'team', 'org'];
    case 'team':
      return ['team', 'org'];
    default:
      return ['org'];
  }
}

/**
 * Normalize a custom Anthropic base URL (proxy / AI gateway) for the
 * Vercel AI SDK's `createAnthropic`, whose `baseURL` includes the version
 * prefix (default `https://api.anthropic.com/v1`) and only appends
 * `/messages`. The official Anthropic SDK — and therefore the
 * `ANTHROPIC_BASE_URL` values users already have from agents — takes the
 * bare origin and adds `/v1/messages` itself. Accept both conventions from
 * one stored value: append `/v1` to the URL *path* unless it already ends
 * in a version segment. Query strings and fragments are preserved in
 * place; a string that does not parse as a URL is returned unchanged.
 */
export function normalizeAnthropicBaseUrl(baseUrl: string): string {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return baseUrl;
  }
  const path = url.pathname.replace(/\/+$/, '');
  url.pathname = /\/v\d+$/.test(path) ? path : `${path}/v1`;
  return url.toString();
}
